import { getEntryPath } from "./accessRoles.js";
import { lazy, Suspense, useState } from "react";
import "./App.css";
import { createBrowserRouter, RouterProvider, redirect, Navigate } from "react-router-dom";
import { getRoleFromAuthToken } from "./jwt_helper.js";
import MyContent from "./components/common/MyContent.js";
import NotFoundPage from "./components/common/NotFoundPage.js";
import RouteErrorPage from "./components/common/RouteErrorPage.js";
import ProtectedRoute from "./components/common/ProtectedRoute.js";
import PageTitle from "./components/common/PageTitle.js";
import PatchNotes from "./components/community/PatchNotes.js";
import { ConfigProvider, theme } from "antd";
import { isUserLoggedIn } from "./axios_helper.js";
import PublicLayout from "./components/public/PublicLayout.js";
import LandingPage from "./components/public/LandingPage.js";
import PublicPatchNotesPage from "./components/public/PublicPatchNotesPage.js";
import AboutPage from "./components/public/AboutPage.js";

const ProtectedGameLayout = lazy(() => import("./components/common/ProtectedGameLayout.js"));
const Me = lazy(() => import("./components/me/Me.js"));
const Company = lazy(() => import("./components/company/Company.js"));
const Map = lazy(() => import("./components/map/Map.js"));
const Codex = lazy(() => import("./components/codex/Codex.js"));
const Community = lazy(() => import("./components/community/Community.js"));
const Employees = lazy(() => import("./components/company/Employees.js"));
const Storage = lazy(() => import("./components/company/Storage.js"));
const Production = lazy(() => import("./components/company/Production.js"));
const Realestates = lazy(() => import("./components/company/Realestates.js"));
const Activity = lazy(() => import("./components/company/Activity.js"));
const Market = lazy(() => import("./components/company/Market.js"));
const Factors = lazy(() => import("./components/codex/Factors.js"));
const Factor = lazy(() => import("./components/codex/Factor.js"));
const Statistics = lazy(() => import("./components/codex/Statistics.js"));
const Encyclopedia = lazy(() => import("./components/codex/Encyclopedia.js"));
const Home = lazy(() => import("./components/home/Home.js"));
const VerifyEmail = lazy(() => import("./components/home/VerifyEmail.js"));
const EmailVerificationPending = lazy(() => import("./components/home/EmailVerificationPending.js"));
const ForgotPassword = lazy(() => import("./components/home/ForgotPassword.js"));
const Roadmap = lazy(() => import("./components/community/Roadmap.js"));
const Legal = lazy(() => import("./components/community/Legal.js"));
const Wiki = lazy(() => import("./components/community/Wiki.js"));
const Achievements = lazy(() => import("./components/me/Achievements.js"));
const Leaderboards = lazy(() => import("./components/me/Leaderboards.js"));
const Profile = lazy(() => import("./components/me/Profile.js"));
const Character = lazy(() => import("./components/me/Character.js"));
const Science = lazy(() => import("./components/me/Science.js"));
const Season = lazy(() => import("./components/me/Season.js"));
const DailyReport = lazy(() => import("./components/me/DailyReport.js"));
const Settings = lazy(() => import("./components/me/Settings.js"));
const Messages = lazy(() => import("./components/me/Messages.js"));
const AdminRoute = lazy(() => import("./components/admin/AdminRoute.js"));

const Admin = lazy(() => import("./components/admin/Admin.js"));
const MapPage = lazy(() => import("./components/map/MapPage.js"));
const FakeLogin = lazy(() => import("./components/admin/FakeLogin.js"));
const Cache = lazy(() => import("./components/admin/Cache.js"));
const SystemAnnouncement = lazy(() => import("./components/admin/SystemAnnouncement.js"));
const BanUser = lazy(() => import("./components/admin/BanUser.js"));
const TestAccess = lazy(() => import("./components/admin/TestAccess.js"));
const ProductionCopy = lazy(() => import("./components/admin/ProductionCopy.js"));
const NewSeason = lazy(() => import("./components/admin/NewSeason.js"));
const PresetCompany = lazy(() => import("./components/admin/PresetCompany.js"));

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(isUserLoggedIn());
  const isTestEnvironment = process.env.REACT_APP_ENVIRONMENT === "test";
  const showTestFeatures = isTestEnvironment || process.env.NODE_ENV === "development";
  const titlePostfix = "Age of Companies";
  const [rerenderComponentKey, setRerenderComponentKey] = useState(0);

  const rerenderComponent = () => {
    setRerenderComponentKey((prev) => prev + 1); // Triggers full remount
  };

  const checkToken = async () => {
    let loggedIn = isUserLoggedIn();
    if (isAuthenticated !== loggedIn) {
      setIsAuthenticated(loggedIn);
    }
    return true;
  };

  const router = createBrowserRouter([
    {
      path: "/",
      element: <PublicLayout />,
      children: [
        {
          index: true,
          element: <LandingPage />,
          loader: () => isUserLoggedIn() ? redirect(getEntryPath(getRoleFromAuthToken())) : null,
        },
        {
          path: "patch-notes",
          element: <PublicPatchNotesPage />,
        },
        {
          path: "about",
          element: <AboutPage />,
        },
        {
          path: "home/forgot-password",
          element: (
            <Suspense fallback={<MyContent contentText="Loading page..." />}>
              <ForgotPassword />
            </Suspense>
          ),
          loader: checkToken,
        },
        {
          path: "home/verify-email",
          element: <Suspense fallback={<MyContent contentText="Loading page..." />}><VerifyEmail /></Suspense>,
          loader: checkToken,
        },
        {
          path: "home/verify-email-pending",
          element: <Suspense fallback={<MyContent contentText="Loading page..." />}><EmailVerificationPending /></Suspense>,
          loader: checkToken,
        },
      ],
    },
    {
      path: "/home",
      element: (
        <Suspense fallback={<MyContent contentText="Loading page..." />}>
          <Home />
        </Suspense>
      ),
      children: [
        {
          index: true,
          loader: async () => redirect("/home/login"),
        },
        {
          path: "/home/login",
          loader: async () => redirect("/"),
        },
        {
          path: "/home/register",
          loader: async () => redirect("/?auth=register"),
        },
      ],
    },
    {
      element: (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Suspense fallback={<MyContent contentText="Loading game..." />}>
            <ProtectedGameLayout isAuthenticated={isAuthenticated} />
          </Suspense>
        </ProtectedRoute>
      ),
      errorElement: <RouteErrorPage />,
      children: [
        { path: "/tutorial", element: <Navigate to="/me/dailyreport" replace />, loader: checkToken },
        { path: "/create-company", element: <Navigate to="/me/dailyreport" replace />, loader: checkToken },
        {
          path: "/me",
          element: <Me />,
          children: [
            {
              index: true,
              loader: async () => redirect("/me/dailyreport"),
            },
            {
              path: "/me/science",
              element: <><PageTitle title={"Science - " + titlePostfix} /><Science /></>,
              loader: checkToken,
            },
            {
              path: "/me/season",
              element: <><PageTitle title={"Season - " + titlePostfix} /><Season /></>,
              loader: checkToken,
            },
            {
              path: "/me/character",
              element: (
                <>
                  <PageTitle title={"Character - " + titlePostfix} />
                  <Character />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/dailyreport",
              element: (
                <>
                  <PageTitle title={"Daily report - " + titlePostfix} />
                  <DailyReport />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/achievements",
              element: (
                <>
                  <PageTitle title={"Achievements - " + titlePostfix} />
                  <Achievements />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/profile/user/:userId",
              element: (
                <>
                  <PageTitle title={"Profile - " + titlePostfix} />
                  <Profile />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/leaderboards",
              element: (
                <>
                  <PageTitle title={"Leaderboards - " + titlePostfix} />
                  <Leaderboards />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/messages",
              element: (
                <>
                  <PageTitle title={"Messages - " + titlePostfix} />
                  <Messages />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/messages/:userId",
              element: (
                <>
                  <PageTitle title={"Messages - " + titlePostfix} />
                  <Messages />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/me/settings",
              element: (
                <>
                  <PageTitle title={"Settings - " + titlePostfix} />
                  <Settings />
                </>
              ),
              loader: checkToken,
            },
          ],
        },
        {
          path: "/company",
          element: <Company />,
          children: [
            {
              index: true,
              loader: async () => redirect("/company/activity/events"),
            },
            {
              path: "/company/events",
              loader: async () => redirect("/company/activity/events"),
            },
            {
              path: "/company/transactions",
              loader: async () => redirect("/company/activity/transactions"),
            },
            {
              path: "/company/activity",
              loader: async () => redirect("/company/activity/events"),
            },
            {
              path: "/company/activity/:activityTab",
              element: (
                <>
                  <PageTitle title={"Activity - " + titlePostfix} />
                  <Activity />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/realestates",
              element: (
                <>
                  <PageTitle title={"Manage Property - " + titlePostfix} />
                  <Realestates key={rerenderComponentKey} rerenderComponent={rerenderComponent} />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/production",
              element: (
                <>
                  <PageTitle title={"Production - " + titlePostfix} />
                  <Production />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/storage",
              element: (
                <>
                  <PageTitle title={"Storage - " + titlePostfix} />
                  <Storage />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/employees",
              element: (
                <>
                  <PageTitle title={"Employees - " + titlePostfix} />
                  <Employees />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/construction",
              loader: async () => redirect("/company/realestates"),
            },
            {
              path: "/company/market",
              element: (
                <>
                  <PageTitle title={"Market - " + titlePostfix} />
                  <Market />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/company/trade",
              loader: async () => redirect("/company/market"),
            },
          ],
        },
        {
          path: "/map",
          element: <Map />,
          children: [
            {
              index: true,
              element: (
                <>
                  <PageTitle title={"World Map - " + titlePostfix} />
                  <Suspense fallback={<MyContent contentText="Loading map..." />}>
                    <MapPage />
                  </Suspense>
                </>
              ),
              loader: checkToken,
            },
          ],
        },
        {
          path: "/codex",
          element: <Codex />,
          children: [
            {
              index: true,
              loader: async () => redirect("/codex/encyclopedia"),
            },
            {
              path: "/codex/factors/:tileId?",
              element: (
                <>
                  <PageTitle title={"Factors - " + titlePostfix} />
                  <Factors />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/codex/factors/:tileId/:factorId",
              element: (
                <>
                  <PageTitle title={"Factors - " + titlePostfix} />
                  <Factor />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/codex/statistics",
              element: (
                <>
                  <PageTitle title={"Statistics - " + titlePostfix} />
                  <Statistics />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/codex/encyclopedia",
              element: (
                <>
                  <PageTitle title={"Encyclopedia - " + titlePostfix} />
                  <Encyclopedia />
                </>
              ),
              loader: checkToken,
            },
          ],
        },
        {
          path: "/community",
          element: <Community />,
          children: [
            {
              index: true,
              loader: async () => redirect("/community/wiki"),
            },
            {
              path: "/community/wiki",
              element: (
                <>
                  <PageTitle title={"Wiki - " + titlePostfix} />
                  <Wiki />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/community/roadmap",
              element: (
                <>
                  <PageTitle title={"Roadmap - " + titlePostfix} />
                  <Roadmap />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/community/patchnotes",
              element: (
                <>
                  <PageTitle title={"Patch notes - " + titlePostfix} />
                  <PatchNotes />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/community/legal",
              element: (
                <>
                  <PageTitle title={"Legal - " + titlePostfix} />
                  <Legal />
                </>
              ),
              loader: checkToken,
            },
          ],
        },
        {
          path: "/admin",
          element: (
            <AdminRoute>
              <Suspense fallback={null}>
                <Admin showTestFeatures={showTestFeatures} />
              </Suspense>
            </AdminRoute>
          ),
          children: [
            {
              index: true,
              loader: async () => redirect("/admin/fake-login"),
            },
            {
              path: "/admin/fake-login",
              element: (
                <>
                  <PageTitle title={"Fake login - " + titlePostfix} />
                  <FakeLogin />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/cache",
              element: (
                <>
                  <PageTitle title={"Cache - " + titlePostfix} />
                  <Cache />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/system-announcement",
              element: (
                <>
                  <PageTitle title={"System announcement - " + titlePostfix} />
                  <SystemAnnouncement />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/ban-user",
              element: (
                <>
                  <PageTitle title={"Ban user - " + titlePostfix} />
                  <BanUser />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/test-access",
              element: (
                <>
                  <PageTitle title={"Test access - " + titlePostfix} />
                  <TestAccess />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/production-copy",
              element: (
                <>
                  <PageTitle title={"Production copy - " + titlePostfix} />
                  <ProductionCopy />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/new-season",
              element: (
                <>
                  <PageTitle title={"New season - " + titlePostfix} />
                  <NewSeason />
                </>
              ),
              loader: checkToken,
            },
            {
              path: "/admin/next-wonder",
              element: <Navigate to="/admin/new-season" replace />,
              loader: checkToken,
            },
            {
              path: "/admin/world-reset",
              element: <Navigate to="/admin/new-season" replace />,
              loader: checkToken,
            },
            ...(showTestFeatures ? [{
              path: "/admin/preset-company",
              element: (
                <>
                  <PageTitle title={"Preset company - " + titlePostfix} />
                  <PresetCompany />
                </>
              ),
              loader: checkToken,
            }] : []),
          ],
        },
      ],
    },
    {
      element: isAuthenticated ? (
        <ProtectedRoute isAuthenticated={isAuthenticated}>
          <Suspense fallback={<MyContent contentText="Loading game..." />}>
            <ProtectedGameLayout isAuthenticated={isAuthenticated} />
          </Suspense>
        </ProtectedRoute>
      ) : <PublicLayout />,
      loader: checkToken,
      children: isAuthenticated ? [
        { element: <Company />, children: [{ path: "company/*", element: <NotFoundPage /> }] },
        { element: <Codex />, children: [{ path: "codex/*", element: <NotFoundPage /> }] },
        { element: <Map />, children: [{ path: "map/*", element: <NotFoundPage /> }] },
        { element: <Community />, children: [{ path: "community/*", element: <NotFoundPage /> }] },
        { element: <Me />, children: [{ path: "*", element: <NotFoundPage /> }] },
      ] : [{ path: "*", element: <NotFoundPage /> }],
    },
  ]);

  return (
    <ConfigProvider
      theme={{
        // It's possible to combine dark algorithm and compact algorithm
        // algorithm: [theme.darkAlgorithm, theme.compactAlgorithm],
        algorithm: theme.defaultAlgorithm,
      }}
    >
      <div className={`app-background${isTestEnvironment ? " app-background--test" : ""}`}>
        <RouterProvider router={router} />
      </div>
    </ConfigProvider>
  );
};
export default App;
